import React, { memo } from "react";
import useEventEditForm from "./index.hooks";
import { Event } from "models";
import { Divider, Grid, Hidden, IconButton } from "@material-ui/core";
import {
  AppButton,
  AppDatePicker,
  AppInteractiveTypography,
  AppPaper,
  SmartphoneEventPreview,
} from "components/index";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ImageIcon from "@material-ui/icons/Image";
import { FieldError } from "react-hook-form";
import UploadFile from "components/UploadFile";

type EventEditFormProps = {
  event: Event;
  toEdit: boolean;
  dialogCloser: () => void;
};

const EventEditForm = ({ event, toEdit, dialogCloser }: EventEditFormProps) => {
  const {
    styles,
    classes,
    register,
    errors,
    onSubmit,
    watch,
    edit,
    handleEdit,
    Fields,
    handleDeleteEdit,
    control,
    editImage,
    handleImageChangerClose,
    uploadLogo,
    isValid,
    isLoading,
    modified,
    goToImageChanger,
    t,
  } = useEventEditForm(toEdit, event);

  return (
    <>
      <Grid container className={styles.mainContainer}>
        <Grid
          item
          md={8}
          sm={12}
          xs={12}
          className={editImage ? styles.hidden : styles.dataContainer}
        >
          <AppPaper className={styles.paper}>
            <form onSubmit={onSubmit}>
              <Grid container justify={"space-between"} spacing={5}>
                <Grid item md={12} xs={12} sm={12}>
                  <AppInteractiveTypography
                    edit={edit.includes(Fields.TITLE) || toEdit}
                    interaction={() => handleEdit(Fields.TITLE)}
                    typoProps={{
                      variant: "h5",
                      color: "primary",
                      align: "center",
                    }}
                    inputProps={{
                      variant: "outlined",
                      fullWidth: true,
                      onBlur: () => {
                        if (!!!errors.title) handleDeleteEdit(Fields.TITLE);
                      },
                      label: t("Titolo"),
                      name: "title",
                      inputRef: register,
                      error: !!errors.title,
                      helperText: errors?.title?.message,
                      maxChars: 30,
                      currentCount: watch("title", event.title).length,
                      value: watch("title", event.title),
                    }}
                  />
                  <Divider variant={"middle"} style={{ marginTop: "2em" }} />
                </Grid>
                <Grid item md={12} xs={12} sm={12}>
                  <div className={styles.contentTypo}>
                    <AppInteractiveTypography
                      edit={edit.includes(Fields.CONTENT) || toEdit}
                      interaction={() => handleEdit(Fields.CONTENT)}
                      badgePos={{ t: -38, r: 180 }}
                      typoText={watch("content", event.content)}
                      typoLabel={t("Descrizione:")}
                      typoProps={{
                        variant: "h6",
                        align: "center",
                        style: { fontSize: 18, marginBottom: "1em" },
                      }}
                      inputProps={{
                        variant: "outlined",
                        multiline: true,
                        name: "content",
                        label: t("Descrizione"),
                        rows: 4,
                        inputRef: register,
                        error: !!errors.content,
                        helperText: errors?.content?.message,
                        maxChars: 200,
                        currentCount: watch("content", event.content).length,
                        onBlur: () => {
                          if (!!!errors.content)
                            handleDeleteEdit(Fields.CONTENT);
                        },
                        value: watch("content", event.content),
                        fullWidth: true,
                      }}
                    />
                  </div>
                  <Divider variant={"middle"} style={{ marginTop: "2em" }} />
                </Grid>
                <Grid item md={6} xs={12} sm={6}>
                  <AppDatePicker
                    control={control}
                    defaultValue={watch("validFrom", event.validFrom)}
                    label={t("Data di inizio")}
                    name="validFrom"
                    error={!!errors["validFrom"]}
                    helperText={
                      ((errors["validFrom"] as unknown) as FieldError)?.message
                    }
                  />
                </Grid>
                <Grid item md={6} xs={12} sm={6}>
                  <AppDatePicker
                    control={control}
                    defaultValue={watch("expire", event.expire)}
                    label={t("Data di fine")}
                    minDate={watch("validFrom", event.validFrom)}
                    name="expire"
                    error={!!errors["expire"]}
                    helperText={
                      ((errors["expire"] as unknown) as FieldError)?.message
                    }
                  />
                </Grid>
              </Grid>
            </form>
            <div className={styles.actionButtons}>
              <div style={{ flexGrow: 1, justifyItems: "center" }}>
                <AppButton
                  variant={"contained"}
                  color={"primary"}
                  className={
                    toEdit || modified ? styles.saveButton : styles.hidden
                  }
                  disabled={isLoading || !isValid}
                  onClick={onSubmit}
                >
                  {t("Salva")}
                </AppButton>
              </div>
              <Hidden mdUp implementation={"css"}>
                <AppButton
                  variant={"contained"}
                  color={"secondary"}
                  className={styles.imageButton}
                  onClick={goToImageChanger}
                  startIcon={<ImageIcon />}
                >
                  {t("Banner")}
                </AppButton>
              </Hidden>
            </div>
          </AppPaper>
        </Grid>
        <Grid
          item
          md={8}
          sm={12}
          xs={12}
          className={!editImage ? styles.hidden : styles.imageContainer}
        >
          <AppPaper className={styles.paper}>
            <IconButton
              onClick={handleImageChangerClose}
              className={styles.backButton}
            >
              <ArrowBackIosIcon />
            </IconButton>
            <UploadFile
              maxFileSize={1000000}
              filesLimit={1}
              label={t("Banner dell'evento")}
              name="imageUri"
              dropzoneText={t(
                "Trascina qui un'immagine o clicca per caricarne una!",
              )}
              onChange={uploadLogo}
              error={!!errors["imageUri"]}
              helperText={errors["imageUri"]?.message}
              inputRef={register}
            />
          </AppPaper>
        </Grid>
        <Grid container item md={4} sm={false} xs={false}>
          <SmartphoneEventPreview
            isInEditDialog
            event={
              new Event({
                title: watch("title", event.title),
                content: watch("content", event.content),
                imageUri: watch("imageUri", event.imageUri),
                validFrom: watch("validFrom", event.validFrom).toISOString(),
                expire: watch("expire", event.expire).toISOString(),
              })
            }
          />
        </Grid>
      </Grid>
      <AppButton
        color={"secondary"}
        className={styles.closeButton}
        onClick={dialogCloser}
      >
        {t("Chiudi")}
      </AppButton>
    </>
  );
};

export default memo(EventEditForm);
